import {trackTraceBranding} from '../../utils/trackTraceBranding.js'
import {DEFAULT_DELIVERY_DAY_COUNT, DEFAULT_PICKUP_PERIOD, TrackTraceActions, TrackTraceLanguage, TrackTraceMap, TrackTraceMapOption, TrackTraceMessageKey, TrackTracePickupPoint, TrackTraceState, TrackTraceView, defaultSafePlaceOptions} from '../../types/trackTrace.js'

// label is a key of utils/Languages.ts, the preview lives in the planner
export const sampleStates: {state: TrackTraceState, label: string}[] = [
    {state: 'expected', label: 'ttsStateExpected'},
    {state: 'scheduled', label: 'ttsStateScheduled'},
    {state: 'onTheWay', label: 'ttsStateOnTheWay'},
    {state: 'completed', label: 'ttsStateCompleted'},
    {state: 'notCompleted', label: 'ttsStateNotHome'},
    {state: 'returning', label: 'ttsStateReturning'},
    {state: 'onHold', label: 'ttsStateOnHold'},
    {state: 'cancelled', label: 'ttsStateCancelled'}
]

const messageKeys: Partial<Record<TrackTraceState, TrackTraceMessageKey>> = {
    notCompleted: 'deliveryNotHomeRescheduleMessage',
    returning: 'deliveryNotHomeRetourMessage',
    onHold: 'deliveryNotHomeOnHoldMessage',
    cancelled: 'deliveryCancelledMessage'
}

const steps: Record<TrackTraceState, TrackTraceView['step']> = {
    expected: 0, scheduled: 1, onTheWay: 2, completed: 3, notCompleted: 2, returning: 2, onHold: 2, cancelled: 1
}

function isoDate(offset: number) {
    const date = new Date()
    date.setDate(date.getDate() + offset)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function timestamp(dayOffset: number, time: string) {
    const [hours, minutes] = time.split(':').map(Number)
    const date = new Date()
    date.setDate(date.getDate() + dayOffset)
    date.setHours(hours, minutes, 0, 0)
    return date.toISOString()
}

export type SampleChoice = {choice: TrackTraceActions['choice'], instructionsText: string}

const OPEN: TrackTraceState[] = ['expected', 'scheduled', 'onTheWay', 'notCompleted', 'onHold']

// The next working days from tomorrow, the preview has no opening days of the reseller
function weekdays(count: number): string[] {
    const days: string[] = []

    for (let offset = 1; days.length < count; offset++) {
        const date = new Date()
        date.setDate(date.getDate() + offset)

        if (date.getDay() !== 0 && date.getDay() !== 6) {
            days.push(isoDate(offset))
        }
    }

    return days
}

// Same rules as buildActions on the server, for a shipment without hand-over requirements
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sampleActions(reseller: any, state: TrackTraceState, sample: SampleChoice): TrackTraceActions | null {
    const settings = reseller.settings.orders?.trackTrace || {}
    const enabled = settings.actions || {}

    const pickupPoints: TrackTracePickupPoint[] = (settings.pickupPoints || []).filter((point: TrackTracePickupPoint) => point.id && point.name?.trim())
    const offersPickup = !!enabled.pickupPoint && pickupPoints.length > 0

    if (!enabled.safePlace && !enabled.neighbour && !offersPickup && !enabled.instructions && !enabled.deliveryDay) {
        return null
    }

    const open = OPEN.includes(state)
    const action = {available: open, blockedBy: open ? null : 'closed' as const}
    const pickup = open && state !== 'onHold' && state !== 'expected' ? action : {available: false, blockedBy: state === 'expected' ? 'notAtDepot' as const : 'closed' as const}
    const options = (settings.safePlaceOptions || []).filter(Boolean)

    // Another delivery day until the route is ready; on the way the route already started
    const deliveryDayOpen = ['expected', 'scheduled', 'notCompleted', 'onHold'].includes(state)

    return {
        safePlace: enabled.safePlace ? action : null,
        neighbour: enabled.neighbour ? action : null,
        pickupPoint: offersPickup ? pickup : null,
        instructions: enabled.instructions ? action : null,
        deliveryDay: enabled.deliveryDay ? {available: deliveryDayOpen, blockedBy: deliveryDayOpen ? null : 'closed'} : null,
        safePlaceOptions: options.length ? options : [...defaultSafePlaceOptions],
        pickupPoints: offersPickup ? pickupPoints : [],
        pickupUntil: offersPickup ? isoDate((settings.pickupPeriod || DEFAULT_PICKUP_PERIOD) + 2) : '',
        deliveryDays: enabled.deliveryDay && deliveryDayOpen ? weekdays(settings.deliveryDayCount || DEFAULT_DELIVERY_DAY_COUNT) : [],
        choice: sample.choice,
        instructionsText: sample.instructionsText
    }
}

// Utrecht, the courier a few streets away on the way to the address
function sampleMap(option: TrackTraceMapOption | undefined, state: TrackTraceState): TrackTraceMap | null {
    if (!option || option === 'none' || !['expected', 'scheduled', 'onTheWay', 'notCompleted'].includes(state)) {
        return null
    }

    const withCourier = state === 'onTheWay' && option !== 'recipient'

    return {
        recipient: {lat: 52.0907, lng: 5.1214},
        courier: withCourier ? {lat: 52.0852, lng: 5.1102, updatedAt: new Date().toISOString()} : null,
        stopsBefore: state === 'onTheWay' && option === 'recipientCourierStops' ? 3 : null,
        eta: withCourier ? '14:35' : null
    }
}

/** Example shipment for the preview in the settings, with the reseller's current (unsaved) settings. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sampleView(reseller: any, state: TrackTraceState, language: TrackTraceLanguage, sample: SampleChoice = {choice: null, instructionsText: ''}): TrackTraceView {
    const settings = reseller.settings.orders?.trackTrace || {}
    const messageKey = messageKeys[state]
    const messageText = messageKey ? (language === 'nl' ? settings[messageKey] : settings.translations?.[language]?.[messageKey]) : ''

    const timeline: TrackTraceView['timeline'] = [{type: 'created', timestamp: timestamp(-2, '16:02')}]

    if (state !== 'expected') timeline.push({type: 'atDepot', timestamp: timestamp(-1, '19:15')})
    if (state !== 'expected') timeline.push({type: 'planned', timestamp: timestamp(-1, '21:40')})
    if (['onTheWay', 'completed', 'notCompleted', 'returning', 'onHold'].includes(state)) timeline.push({type: 'onTheWay', timestamp: timestamp(0, '08:12')})
    if (state === 'completed') timeline.push({type: 'completed', timestamp: timestamp(0, '14:32')})
    if (['notCompleted', 'returning', 'onHold'].includes(state)) timeline.push({type: 'notCompleted', timestamp: timestamp(0, '14:32'), reason: 'nothome'})
    if (state === 'returning') timeline.push({type: 'returned', timestamp: timestamp(0, '18:05')})
    if (state === 'onHold') timeline.push({type: 'onHold', timestamp: timestamp(0, '18:05')})
    if (state === 'cancelled') timeline.push({type: 'cancelled', timestamp: timestamp(0, '09:30')})

    const colloState = {expected: 'expected', scheduled: 'atDepot', onTheWay: 'onTheWay', completed: 'completed', notCompleted: 'notCompleted', returning: 'returned', onHold: 'onHold', cancelled: 'cancelled'}[state] as TrackTraceView['colli'][0]['state']

    return {
        trackTrace: 'VOORBEELD123',
        addressIndex: 0,
        addressType: 'delivery',
        state,
        step: steps[state],
        date: state === 'expected' || state === 'notCompleted' || state === 'returning' || state === 'cancelled' ? null : state === 'scheduled' ? isoDate(1) : state === 'onHold' ? isoDate(7) : isoDate(0),
        window: state === 'scheduled' ? {from: '13:00', to: '17:00'} : state === 'onTheWay' ? {from: '14:20', to: '15:20'} : null,
        arrivedAt: state === 'completed' ? '14:32' : null,
        message: messageKey ? {key: messageKey, text: typeof messageText === 'string' ? messageText : ''} : null,
        outcome: null,
        requirements: {signature: false, statedAddressOnly: false, mailbox: false, minimumAge: null, idCheck: false, verificationCode: false},
        city: 'Utrecht',
        senderName: 'Voorbeeldwinkel',
        colli: [{state: colloState}, {state: colloState}],
        timeline,
        map: sampleMap(settings.map, state),
        details: {
            address: {name: 'J. Jansen', attention: '', street: 'Oudegracht', nr: '112', addition: '', street2: '', postalCode: '3511 AX', city: 'Utrecht', country: 'NL'},
            reference: 'ORD-20418',
            colli: [{barcode: 'VOORBEELD123-1', type: 'Doos'}, {barcode: 'VOORBEELD123-2', type: 'Doos'}],
            proof: state === 'completed' ? {signee: 'J. Jansen', signature: null, images: []} : null,
            actions: sampleActions(reseller, state, sample)
        },
        branding: trackTraceBranding(reseller)
    }
}
